import React, { useState, useEffect, useRef } from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import { Icon } from "@iconify/react";
import { API_URL } from "..";
import swal from "sweetalert";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

mapboxgl.accessToken = 'pk.eyJ1IjoiemFtcCIsImEiOiJjbTkxa3dkcnMwMmQxMnJzYzBobW9hOHBiIn0.GWvvqPrWqtMby0HYCYdjMQ';

const VehicleTruckLocation = () => {
  const [licensePlate, setLicensePlate] = useState("");
  const [vehicleLocation, setVehicleLocation] = useState(null);
  const [driverInfo, setDriverInfo] = useState(null);
  const [truckInfo, setTruckInfo] = useState(null);
  const [showAlertMessage, setShowAlertMessage] = useState({
    status: false,
    message: "",
  });

  const mapContainer = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);

  const handleLicensePlateChange = (e) => {
    setLicensePlate(e.target.value);
  };

  const handleSearch = async () => {
    if (!licensePlate.trim()) {
      setShowAlertMessage({
        message: "Please enter a license plate number",
        status: true,
      });
      return;
    }

    try {
      const user = JSON.parse(localStorage.getItem('user'))
      const apiCall = await fetch(`${API_URL}/api/truck/truck-with-location-single-with-plate`, {
        method: "POST",
        headers: {
          "Accept": "*/*",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ plate_no: licensePlate , user_id : user.id}),
      });

      const response = await apiCall.json();

      if (apiCall.status === 200 && response.data.length !== 0) {
        const truckData = response.data[0];
        setTruckInfo(truckData);
        setDriverInfo(truckData.driver || null);

        if (truckData.location) {
          const location = {
            lat: parseFloat(truckData.location.latitude),
            lng: parseFloat(truckData.location.longitude),
          };
          setVehicleLocation(location);
        }
      } else {
        swal(
          "No Location Found",
          "Your truck's location has not been recorded yet. Please ask the assigned driver to check in through the app.",
          "warning"
        );
        setVehicleLocation(null);
      }
    } catch (error) {
      console.error("Error fetching truck location:", error);
      swal("Error", "Failed to fetch truck data. Please try again later.", "error");
    }
  };

  const initializeMap = (location) => {
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/streets-v11",
      center: [location.lng, location.lat],
      zoom: 12,
    });

    marker.current = new mapboxgl.Marker({
      color: "#FF0000",
      scale: 1.5,
    })
      .setLngLat([location.lng, location.lat])
      .addTo(map.current);

    map.current.addControl(new mapboxgl.NavigationControl(), "top-right");
  };

  const updateMap = (location) => {
    if (!map.current) return;

    map.current.flyTo({
      center: [location.lng, location.lat],
      essential: true,
    });

    if (marker.current) {
      marker.current.setLngLat([location.lng, location.lat]);
    } else {
      marker.current = new mapboxgl.Marker({
        color: "#FF0000",
        scale: 1.5,
      })
        .setLngLat([location.lng, location.lat])
        .addTo(map.current);
    }
  };

  useEffect(() => {
    if (vehicleLocation && mapContainer.current) {
      if (!map.current) {
        initializeMap(vehicleLocation);
      } else {
        updateMap(vehicleLocation);
      }
    }
  }, [vehicleLocation]);

  useEffect(() => {
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
        marker.current = null;
      }
    };
  }, []);

  return (
    <MasterLayout>
      <Breadcrumb title="Vehicle Location Tracker" />
      <div className="container mt-4">
        <div className="card p-4 shadow-sm rounded mb-4">
          <div className="row justify-content-center mt-5">
            <div className="col-lg-6 text-end">
              <label className="fw-bold mb-2">License Plate Number:</label>
              <div className="license-plate-input mb-3">
                <input
                  type="text"
                  className="form-control text-center fw-bold"
                  placeholder="ABC-1234"
                  value={licensePlate}
                  onChange={handleLicensePlateChange}
                />
              </div>
            </div>
            <div className="col-lg-4 text-start" style={{ marginTop: 10 }}>
              <button className="btn btn-primary" onClick={handleSearch}>
                Search
              </button>
            </div>
          </div>
          {showAlertMessage.status && (
            <div className="alert alert-danger mt-3 text-center">
              {showAlertMessage.message}
            </div>
          )}
        </div>

        {vehicleLocation && (
          <div className="row mt-3">
            <div className="col-md-6">
              <div className="card p-3 shadow-sm rounded">
                <h6 className="text-secondary fw-bold">Driver Information</h6>
                <p><strong>Name:</strong> {driverInfo?.last_name + ' ' + driverInfo?.last_name || "N/A"}</p>
                <p><strong>Phone:</strong> {driverInfo?.phone_number || "N/A"}</p>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card p-3 shadow-sm rounded">
                <h6 className="text-secondary fw-bold">Truck Information</h6>
                <p><strong>Truck Number:</strong> {truckInfo?.truck_name || "N/A"}</p>
                <p><strong>Truck Type:</strong> {truckInfo?.truck_type || "N/A"}</p>
              </div>
            </div>
            <div className="col-lg-12 mt-3">
              <div className="card p-3 shadow-sm rounded">
                <h5 className="text-secondary fw-bold mb-3">Vehicle Location</h5>
                <div
                  ref={mapContainer}
                  style={{ width: "100%", height: "400px", borderRadius: "8px" }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <style>
        {`
        .license-plate-input input {
          border: 2px solid #000;
          border-radius: 8px;
          font-size: 1.2rem;
          letter-spacing: 2px;
          text-transform: uppercase;
          background: #f8f9fa;
          padding: 10px;
        }
        .license-plate-input {
          display: inline-block;
          padding: 5px;
          border: 2px solid #333;
          background: white;
          border-radius: 10px;
        }
        .card {
          border: none;
          border-radius: 12px;
        }
        .mapboxgl-ctrl {
          margin: 10px;
        }
        `}
      </style>
    </MasterLayout>
  );
};

export default VehicleTruckLocation;
